import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "./api";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: any | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<any | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // -----------------------------
  // 1. Refresh token function
  // -----------------------------
  const refreshInFlight = useRef<Promise<any | null> | null>(null);

  const refresh = () => {
    if (!refreshInFlight.current) {
      refreshInFlight.current = api
        .post("/v1/auth/refresh")
        .then((res) => {
          setUser(res.data);
          return res.data;
        })
        .catch(() => {
          setUser(null);
          return null;
        })
        .finally(() => {
          refreshInFlight.current = null;
        });
    }
    return refreshInFlight.current;
  };

  // -----------------------------
  // 2. Silent login on app load
  // -----------------------------
  useEffect(() => {
    api
      .get("/v1/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);


  // -----------------------------
  // 3. Login
  // -----------------------------
  const login = async (username: string, password: string) => {
    await api.post("/v1/auth/login", { userName: username, password });
    const res = await api.get("/v1/auth/me");
    setUser(res.data);
  };

  // -----------------------------
  // 4. Logout
  // -----------------------------
  const logout = async () => {
    try {
      await api.post("/v1/auth/logout");
    } catch (e) {
      console.error("Logout failed", e);
    }
    setUser(null);
    navigate("/login", { replace: true });
  };

  // -----------------------------
  // 5. Axios interceptor for auto-refresh
  // -----------------------------
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // ❗ Do NOT refresh or redirect on auth endpoints
        const isAuthEndpoint =
          originalRequest.url?.includes("/auth/login") ||
          originalRequest.url?.includes("/auth/refresh") ||
          originalRequest.url?.includes("/auth/me");

        if (isAuthEndpoint) {
          return Promise.reject(error);
        }

        // Normal refresh logic
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const newUser = await refresh();
          if (newUser) {
            return api(originalRequest); // retry original request
          }

          navigate("/login", { replace: true });
        }

        return Promise.reject(error);
      }
    );

    return () => api.interceptors.response.eject(interceptor);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext)!;